import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SupportCases.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const caseTypesList = {
    'BugReport': 'Bug Report (Spiel/Server Fehler)',
    'SaltyChat': 'SaltyChat Problem/Fehler',
    'PlayerReport': 'Spieler Report (Regelverstoß)',
    'TechIssue': 'Technisches Problem (Client/Hardware)',
    'Other': 'Anderes/Sonstiges',
};

const statusInfoMap = {
    'open': { text: '⚠️ Offen', class: 'supportcases-pill open' },
    'solved': { text: '✅ Gelöst', class: 'supportcases-pill solved' },
};

const SupportCases = ({ userData, hasPermission }) => {
    const [activeTab, setActiveTab] = useState('user');
    const [subjectDiscordId, setSubjectDiscordId] = useState('');
    const [caseHistory, setCaseHistory] = useState([]);
    const [allOpenCases, setAllOpenCases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [newCaseTypes, setNewCaseTypes] = useState([]);
    const [newStatus, setNewStatus] = useState('open');
    const [newComment, setNewComment] = useState('');
    const [selectedCaseId, setSelectedCaseId] = useState('');
    const [solveComment, setSolveComment] = useState('');

    // Allow all authenticated users to view and create support cases
    const canAccessSupport = userData?.id;

    useEffect(() => {
        if (subjectDiscordId && /^\d{17,20}$/.test(subjectDiscordId)) {
            fetchCaseHistory(subjectDiscordId);
        }
    }, [subjectDiscordId]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const discordId = urlParams.get('subject_discord_id');
        if (discordId && /^\d{17,20}$/.test(discordId)) {
            setSubjectDiscordId(discordId);
            setActiveTab('user');
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'all') {
            fetchAllOpenCases();
        }
    }, [activeTab]);

    const fetchCaseHistory = async (discordId) => {
        if (!discordId || !/^\d{17,20}$/.test(discordId)) return;
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/support-cases/${discordId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCaseHistory(response.data.cases || []);
            } else {
                setError(response.data.error || 'Fehler beim Laden');
            }
        } catch (err) {
            setError('Supportfälle konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllOpenCases = async () => {
        try {
            setLoadingAll(true);
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/support-cases/open`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setAllOpenCases(response.data.cases || []);
            }
        } catch (err) {
            console.error('Fehler beim Laden der offenen Fälle:', err);
        } finally {
            setLoadingAll(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (subjectDiscordId && /^\d{17,20}$/.test(subjectDiscordId)) {
            fetchCaseHistory(subjectDiscordId);
            window.history.pushState({}, '', `?subject_discord_id=${subjectDiscordId}`);
        } else {
            setError('Bitte ID eingeben.');
        }
    };

    const handleSaveCase = async (e) => {
        e.preventDefault();
        if (newCaseTypes.length === 0) {
            setMessage({ type: 'warning', text: 'Bitte Fall-Typ auswählen.' });
            return;
        }
        try {
            setLoading(true);
            const token = localStorage.getItem('discord_token');
            const response = await axios.post(`${API_BASE}/api/support-cases`, {
                subject_discord_id: subjectDiscordId,
                case_types: newCaseTypes,
                status: newStatus,
                comment: newComment
            }, { headers: { Authorization: `Bearer ${token}` } });

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Gespeichert.' });
                setNewCaseTypes([]);
                setNewStatus('open');
                setNewComment('');
                fetchCaseHistory(subjectDiscordId);
            }
        } catch (err) {
            setMessage({ type: 'danger', text: 'Fehler beim Speichern.' });
        } finally {
            setLoading(false);
        }
    };

    const handleMarkSolved = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const token = localStorage.getItem('discord_token');
            const response = await axios.put(`${API_BASE}/api/support-cases/${selectedCaseId}/solve`, {
                solve_comment: solveComment
            }, { headers: { Authorization: `Bearer ${token}` } });

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Fall gelöst.' });
                setSelectedCaseId('');
                setSolveComment('');
                if (activeTab === 'user') {
                    fetchCaseHistory(subjectDiscordId);
                } else if (activeTab === 'all') {
                    fetchAllOpenCases();
                }
            }
        } catch (err) {
            setMessage({ type: 'danger', text: 'Fehler.' });
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSolve = async (caseId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('discord_token');
            const response = await axios.put(`${API_BASE}/api/support-cases/${caseId}/solve`, {
                solve_comment: ''
            }, { headers: { Authorization: `Bearer ${token}` } });

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Fall gelöst.' });
                fetchAllOpenCases();
            }
        } catch (err) {
            setMessage({ type: 'danger', text: 'Fehler.' });
        } finally {
            setLoading(false);
        }
    };

    const handleCaseTypeChange = (typeKey, checked) => {
        if (checked) setNewCaseTypes([...newCaseTypes, typeKey]);
        else setNewCaseTypes(newCaseTypes.filter(t => t !== typeKey));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const switchToUserTab = (discordId) => {
        setSubjectDiscordId(discordId);
        setActiveTab('user');
        fetchCaseHistory(discordId);
        window.history.pushState({}, '', `?subject_discord_id=${discordId}`);
    };

    if (!canAccessSupport) {
        return (
            <div className="supportcases-container">
                <div className="supportcases-access-denied">
                    <h2>🚫 Zugriff verweigert</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="supportcases-container">
            {/* Hero Section */}
            <div className="supportcases-hero">
                <div>
                    <h2 className="supportcases-hero-title">🎫 Supportfälle</h2>
                    <p className="supportcases-hero-subtitle">Verwaltung & Historie</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="supportcases-main-card">
                <div className="supportcases-card-header">
                    <h4 className="supportcases-card-title">Support Verwaltung</h4>
                </div>

                <div className="supportcases-tabs">
                <button
                    className={`supportcases-tab ${activeTab === 'user' ? 'active' : ''}`}
                    onClick={() => setActiveTab('user')}
                >
                    User-Historie
                </button>
                <button
                    className={`supportcases-tab ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    Alle offenen Fälle
                </button>
                </div>

            {/* Main Content */}
            <div className="supportcases-content">
                {activeTab === 'user' && (
                    <>
                        {/* Warning Banner */}
                        <div className="supportcases-warning">
                            <strong>Wichtiger Hinweis:</strong> Dokumentieren Sie alle Fälle für die Vollständigkeit der Historie.
                            <br />
                        </div>

                        {/* Search Form */}
                        <div className="supportcases-form-card">
                            <form onSubmit={handleSearch} className="supportcases-form">
                                {message && (
                                    <div className={`supportcases-message ${message.type}`}>
                                        {message.type === 'success' && '✅'}
                                        {message.type === 'danger' && '⚠️'}
                                        {message.type === 'warning' && 'ℹ️'}
                                        {message.text}
                                    </div>
                                )}

                                <div className="supportcases-form-group">
                                    <label className="supportcases-form-label">
                                        Discord User ID <span className="required">*</span>
                                    </label>
                                    <div className="supportcases-form-row">
                                        <input
                                            type="text"
                                            className="supportcases-form-control"
                                            placeholder="Discord User ID eingeben (17-20 Ziffern)"
                                            value={subjectDiscordId}
                                            onChange={(e) => setSubjectDiscordId(e.target.value)}
                                            required
                                            pattern="\d{17,20}"
                                            title="Bitte geben Sie eine gültige Discord User ID ein."
                                        />
                                        <button type="submit" className="supportcases-btn primary" disabled={loading}>
                                            🔍 Suchen
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* New Case Form */}
                        {subjectDiscordId && /^\d{17,20}$/.test(subjectDiscordId) && (
                            <div className="supportcases-form-card">
                                <form onSubmit={handleSaveCase} className="supportcases-form">
                                    <div className="supportcases-info">
                                        Der neue Fall wird für die Discord ID <strong>{subjectDiscordId}</strong> erfasst.
                                    </div>

                                    <div className="supportcases-form-group">
                                        <label className="supportcases-form-label">Typ des Supportfalls (Mehrfachauswahl)</label>
                                        <div className="supportcases-checkbox-grid">
                                            {Object.entries(caseTypesList).map(([key, display]) => (
                                                <label key={key} className="supportcases-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={newCaseTypes.includes(key)}
                                                        onChange={(e) => handleCaseTypeChange(key, e.target.checked)}
                                                    />
                                                    <span>{display}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="supportcases-form-group">
                                        <label className="supportcases-form-label">Fall-Status</label>
                                        <div style={{ display: 'flex', gap: '20px' }}>
                                            <label className="supportcases-checkbox-label" style={{ width: 'auto' }}>
                                                <input
                                                    type="radio"
                                                    name="status"
                                                    value="open"
                                                    checked={newStatus === 'open'}
                                                    onChange={(e) => setNewStatus(e.target.value)}
                                                />
                                                <span>Offen</span>
                                            </label>
                                            <label className="supportcases-checkbox-label" style={{ width: 'auto' }}>
                                                <input
                                                    type="radio"
                                                    name="status"
                                                    value="solved"
                                                    checked={newStatus === 'solved'}
                                                    onChange={(e) => setNewStatus(e.target.value)}
                                                />
                                                <span>Gelöst</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="supportcases-form-group">
                                        <label className="supportcases-form-label">Beschreibung des Supportfalls</label>
                                        <textarea
                                            className="supportcases-form-control"
                                            rows="4"
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            placeholder="Detaillierte Beschreibung des Problems, wann und wo es aufgetreten ist..."
                                            required
                                        />
                                    </div>

                                    <button className="supportcases-submit success" type="submit" disabled={loading}>
                                        💾 {loading ? 'Wird gespeichert...' : 'Supportfall speichern'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Solve Case Form */}
                        {subjectDiscordId && /^\d{17,20}$/.test(subjectDiscordId) && caseHistory.some(c => c.status !== 'solved') && (
                            <div className="supportcases-form-card">
                                <form onSubmit={handleMarkSolved} className="supportcases-form">
                                    <div className="supportcases-form-group">
                                        <label className="supportcases-form-label">Supportfall auswählen</label>
                                        <select
                                            className="supportcases-form-control"
                                            value={selectedCaseId}
                                            onChange={(e) => setSelectedCaseId(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Supportfall auswählen --</option>
                                            {caseHistory.filter(c => c.status !== 'solved').map(c => (
                                                <option key={c.id} value={c.id}>
                                                    #{c.id} - {c.case_types.split(',').map(typeKey => caseTypesList[typeKey]).filter(Boolean).join(', ')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="supportcases-form-group">
                                        <label className="supportcases-form-label">Lösungskommentar (optional)</label>
                                        <textarea
                                            className="supportcases-form-control"
                                            rows="3"
                                            value={solveComment}
                                            onChange={(e) => setSolveComment(e.target.value)}
                                            placeholder="Optional: Kurze Beschreibung der Lösung..."
                                        />
                                    </div>

                                    <button
                                        className="supportcases-submit success"
                                        type="submit"
                                        disabled={loading}
                                        onClick={(e) => {
                                            if (!window.confirm('Sind Sie sicher, dass Sie diesen Supportfall als gelöst markieren möchten?')) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        ✅ Als gelöst markieren
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Case History */}
                        {subjectDiscordId && /^\d{17,20}$/.test(subjectDiscordId) && (
                            <div className="supportcases-list">
                                <div className="supportcases-list-header">
                                    <h3 className="supportcases-list-title">📋 Historie für ID: {subjectDiscordId}</h3>
                                </div>

                                {loading ? (
                                    <div className="supportcases-loading">
                                        <div className="spinner"></div>
                                        <h3>Supportfälle werden geladen...</h3>
                                    </div>
                                ) : error ? (
                                    <div className="supportcases-empty">
                                        <div className="supportcases-empty-icon">⚠️</div>
                                        <h3>Fehler beim Laden</h3>
                                        <p className="muted">{error}</p>
                                        <button className="supportcases-btn primary" onClick={() => fetchCaseHistory(subjectDiscordId)}>
                                            🔄 Erneut versuchen
                                        </button>
                                    </div>
                                ) : caseHistory.length > 0 ? (
                                    <div>
                                        {caseHistory.map((case_) => (
                                            <div key={case_.id} className="supportcases-case">
                                                <div className="supportcases-case-header">
                                                    <span className="supportcases-case-id">#{case_.id}</span>
                                                    <div className="supportcases-case-info">
                                                        <p><strong>Fall-Typen:</strong> {case_.case_types.split(',').map(typeKey => caseTypesList[typeKey]).filter(Boolean).join(', ')}</p>
                                                        <p><strong>Kommentar:</strong> {case_.comment}</p>
                                                        {case_.solve_comment && (
                                                            <p style={{ color: 'var(--success-color)' }}><strong>Lösung:</strong> {case_.solve_comment}</p>
                                                        )}
                                                    </div>
                                                    <div className="supportcases-case-meta">
                                                        <span className={statusInfoMap[case_.status]?.class}>
                                                            {statusInfoMap[case_.status]?.text || case_.status}
                                                        </span>
                                                        <span className="supportcases-case-date">{formatDate(case_.created_at)}</span>
                                                        <span className="supportcases-case-reporter">von {case_.reporter_username}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="supportcases-empty">
                                        <div className="supportcases-empty-icon">📋</div>
                                        <h3>Keine Supportfälle gefunden</h3>
                                        <p className="muted">Für diese Discord ID wurden noch keine Supportfälle erfasst.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'all' && (
                    <div className="supportcases-list">
                        <div className="supportcases-list-header">
                            <h3 className="supportcases-list-title">📋 Alle offenen Supportfälle</h3>
                        </div>

                        {loadingAll ? (
                            <div className="supportcases-loading">
                                <div className="spinner"></div>
                                <h3>Offene Fälle werden geladen...</h3>
                            </div>
                        ) : allOpenCases.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="supportcases-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Betroffener User</th>
                                            <th>Fall-Typen</th>
                                            <th>Status</th>
                                            <th>Erstellt</th>
                                            <th>Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allOpenCases.map((case_) => (
                                            <tr key={case_.id}>
                                                <td>#{case_.id}</td>
                                                <td>{case_.subject_username} ({case_.subject_discord_id})</td>
                                                <td>{case_.case_types.split(',').map(typeKey => caseTypesList[typeKey]).filter(Boolean).join(', ')}</td>
                                                <td>
                                                    <span className={statusInfoMap[case_.status]?.class}>
                                                        {statusInfoMap[case_.status]?.text || case_.status}
                                                    </span>
                                                </td>
                                                <td>{formatDate(case_.created_at)}</td>
                                                <td>
                                                    <button className="supportcases-btn primary" onClick={() => switchToUserTab(case_.subject_discord_id)} style={{ marginRight: '8px' }}>
                                                        Bearbeiten
                                                    </button>
                                                    <button className="supportcases-btn success" onClick={() => {
                                                        if (window.confirm('Sind Sie sicher, dass Sie diesen Supportfall als gelöst markieren möchten?')) {
                                                            handleQuickSolve(case_.id);
                                                        }
                                                    }}>
                                                        Lösen
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="supportcases-empty">
                                <div className="supportcases-empty-icon">✅</div>
                                <h3>Keine offenen Supportfälle</h3>
                                <p className="muted">Alle Fälle sind bearbeitet.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
            </div>
        </div>
    );
};

export default SupportCases;
